import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { TableProps } from 'rsuite/lib/Table';
import { TablePaginationProps } from 'rsuite/lib/Table/TablePagination';
import { SearchInputProps } from '../../../../../components';
import useDebounce from '../../../../../hooks/useDebounce';
import { checkIfIsLoading } from '../../../../../utils';
import History from '../../../../../utils/History';
import { usePagination, useSort } from '../../hooks';
import { getFiltrosModel } from '../NegociacaoFornecedor/utils';
import { colunaDeGraoOfereceOrdenacao } from './colunaDeGrao';
import { GETNegociacoesSalvas, NegotiationSavedFiltersPayload } from './NegociacoesSalvas.services';
import { NegociacoesSalvasFilterKey, useNegociacoesSalvasFilters } from './components/NegociacoesSalvasFilters';
import { TableNegociacoesSalvasProps } from './components/TableNegociacoesSalvas';

export type UseNegociacoesSalvasProps = {
  search: SearchInputProps;
  filters: ReturnType<typeof useNegociacoesSalvasFilters>;
  table: TableNegociacoesSalvasProps & {
    pagination: TablePaginationProps;
  };
  onNewNegotiation: () => Promise<void>;
};

// A lista abre com a negociação editada mais recentemente no topo. O nome do campo viaja para o
// servidor convertido para snake_case (`last_edited_date`), uma das colunas por onde o serviço
// sabe ordenar — nome que ele não reconhece não vira erro, ele recua calado para a ordem por nome.
const DEFAULT_SORT = {
  type: 'lastEditedDate',
  orderBy: 'desc',
} satisfies {
  type: NonNullable<TableProps['sortColumn']>;
  orderBy: NonNullable<TableProps['sortType']>;
};

export const useNegociacoesSalvas = (): UseNegociacoesSalvasProps => {
  const [sort, { onSort: setSort }] = useSort(DEFAULT_SORT);
  const [totalElements, setTotalElements] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [search, setSearch] = useState('');
  const searchRequestIdRef = useRef(0);
  const filters = useNegociacoesSalvasFilters();
  const debounce = useDebounce();

  const pagination = usePagination({
    total: totalElements,
  });

  const filtersPayload = useMemo<NegotiationSavedFiltersPayload>(
    () => ({
      product: filters.value.productIds ?? [],
      store: filters.value.storeIds ?? [],
      cluster: filters.value.clusters ?? [],
      supplier: filters.value.supplier ?? [],
      owner: filters.value.owner ?? [],
      status: (filters.value.status?.[0] as NegotiationSavedFiltersPayload['status']) ?? '',
      visionSaved: filters.value.visionSaved ?? [],
    }),
    [filters.value],
  );

  const model = getFiltrosModel({
    search,
    filters: filtersPayload,
    sort,
    size: pagination.displayLength,
    page: pagination.requestPage,
  });

  const { data, fetchStatus } = useQuery({
    initialData: {
      content: [],
      totalElements: 0,
    },
    queryKey: ['negociacoes/salvas/get-negociacoes', search, filtersPayload, sort, pagination.activePage, pagination.displayLength],
    queryFn: () =>
      GETNegociacoesSalvas({
        ...model,
        filters: filtersPayload,
      }),
    onSuccess: data => setTotalElements(data.totalElements),
    retry: false,
  });

  const handleClickNewNegotiation = async () => {
    History.push('/ipa/negociacoes/fornecedor');
  };

  const handleSortColumn: NonNullable<TableProps['onSortColumn']> = (sortColumn, sortType) => {
    setSort(sortColumn, sortType);
  };

  const handleChangeSearch = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const nextValue = target.value;
    const currentRequestId = searchRequestIdRef.current + 1;

    searchRequestIdRef.current = currentRequestId;
    setSearchValue(nextValue);

    debounce(() => {
      if (searchRequestIdRef.current !== currentRequestId) return;
      setSearch(nextValue);
      pagination.onChangePage(1);
    });
  };

  const handleFilterChange = (key: NegociacoesSalvasFilterKey, value: string[]) => {
    filters.onChange(key, value);
    pagination.onChangePage(1);
  };

  const handleFilterClean = (key: NegociacoesSalvasFilterKey) => {
    filters.onClean(key);
    pagination.onChangePage(1);
  };

  const handleFilterSelectAll = (key: NegociacoesSalvasFilterKey, checked: boolean) => {
    filters.onSelectAll(key, checked);
    pagination.onChangePage(1);
  };

  const handleClearAllFilters = () => {
    searchRequestIdRef.current += 1;
    setSearch('');
    setSearchValue('');
    filters.onCleanAll();
    pagination.onChangePage(1);
  };

  const handleEditNegotiation = (id: string) => {
    History.push(`/ipa/negociacoes/salvas/${id}`);
  };

  return {
    search: {
      id: 'negotiation-search',
      name: 'negotiation-search',
      type: 'text',
      placeholder: 'Pesquise por uma negociação',
      value: searchValue,
      onChange: handleChangeSearch,
    },
    filters: {
      ...filters,
      onChange: handleFilterChange,
      onClean: handleFilterClean,
      onSelectAll: handleFilterSelectAll,
      onCleanAll: handleClearAllFilters,
    },
    table: {
      data: data.content,
      rowKey: 'id',
      loading: checkIfIsLoading(fetchStatus),
      sortColumn: sort.type,
      sortType: sort.orderBy,
      // Com a lista restrita a um grão só, toda linha exibe o mesmo valor naquela coluna e ordenar não
      // muda nada. Quem sabe o filtro é este gancho; a tabela recebe a resposta pronta.
      colunaDeGraoOrdenavel: colunaDeGraoOfereceOrdenacao(filters.value.visionSaved),
      onSortColumn: handleSortColumn,
      onEditNegotiation: handleEditNegotiation,
      pagination,
    },
    onNewNegotiation: handleClickNewNegotiation,
  };
};
